import { notyf } from '@modules';
import { pubsub } from '@services';

/* global Insider */

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.initFormReferences();
    this.bindFormSubmit();
    this.subscribeToVariantChange();
    this.subscribeToQuantityChange();
  }

  disconnectedCallback() {
    this.unsubscribeVariantChange();
    this.unsubscribeQuantityChange();
  }

  /**
   * Initialize form element references.
   */
  initFormReferences() {
    this.form = this.querySelector('form');
    this.quantityInput = this.form.querySelector('input[name="quantity"]');
    this.variantIdInput = this.form.querySelector('input[name="id"]');
    this.submitButtons = document.querySelectorAll(
      `button[form="${this.form.getAttribute('id')}"]`
    );
  }

  /**
   * Bind submit event listener to the form.
   */
  bindFormSubmit() {
    this.form.addEventListener('submit', (event) => this.onFormSubmit(event));
  }

  /**
   * Subscribe to variant change events.
   */
  subscribeToVariantChange() {
    this.unsubscribeVariantChange = pubsub.subscribe(
      `variant:changed:${this.dataset.productId}`,
      ({ html }) => this.onVariantChange(html)
    );
  }

  /**
   * Subscribe to quantity change events.
   */
  subscribeToQuantityChange() {
    this.unsubscribeQuantityChange = pubsub.subscribe('quantity:change', ({ quantity }) => {
      this.quantityInput.value = quantity;
    });
  }

  /**
   * Handle variant change by replacing form and variant selector.
   * @param {Document} html - The parsed HTML document from the variant change response.
   */
  onVariantChange(html) {
    const newProductForm = html.querySelector(
      `product-form[data-product-id="${this.dataset.productId}"]`
    );
    if (!newProductForm) return;

    this.replaceFormElement(newProductForm);
    this.replaceVariantSelector(newProductForm);
  }

  /**
   * Replace the form element while preserving fields outside the form.
   * @param {HTMLElement} newProductForm - The new product form element from the response.
   */
  replaceFormElement(newProductForm) {
    const newForm = newProductForm.querySelector('form');
    if (!newForm) return;

    this.form.replaceWith(newForm.cloneNode(true));
    this.initFormReferences();
    this.bindFormSubmit();
  }

  /**
   * Replace the variant selector element.
   * @param {HTMLElement} newProductForm - The new product form element from the response.
   */
  replaceVariantSelector(newProductForm) {
    const currentVariantSelect = this.querySelector('variant-select');
    const newVariantSelect = newProductForm.querySelector('variant-select');
    if (currentVariantSelect && newVariantSelect) currentVariantSelect.replaceWith(newVariantSelect.cloneNode(true));
  }

  /**
   * Handle form submission
   * @param {Event} event
   */
  async onFormSubmit(event) {
    event.preventDefault();

    if (!this.validateVariantSelection()) return;
    if (!this.validateRequiredFields()) return;

    const submitButton = event.submitter;
    if (submitButton.hasAttribute('data-out-of-stock')) {
      this.handleOutOfStock(submitButton);
      return;
    }

    this.toggleLoading(true);

    try {
      const formData = this.createFormData();
      const itemData = await this.addToCart(formData);
      this.onSuccess(itemData, formData.items[0].quantity);
    } catch (error) {
      this.onError(error);
    } finally {
      this.toggleLoading(false);
    }
  }

  /**
   * Validate that all required variants are selected.
   * @returns {boolean} - True if all variants are selected; false otherwise.
   */
  validateVariantSelection() {
    const unselected = [...this.querySelectorAll('fieldset.option')].filter(
      (fieldset) => !fieldset.querySelector('input[type="radio"]:checked')
    );

    if (unselected.length === 0) return true;

    unselected.forEach((fieldset) => {
      const legend = fieldset.querySelector('legend');
      legend?.classList.add('text-error');
    });

    this.variantChangeListener();

    return false;
  }

  /**
   * Validate that all required fields are filled.
   * @returns {boolean} - True if all required fields are valid; false otherwise.
   */
  validateRequiredFields() {
    const requiredInputs = [...this.querySelectorAll('[required]')];
    if (requiredInputs.length === 0) return true;

    requiredInputs.forEach((input) => input.removeAttribute('aria-invalid'));

    const invalidInputs = requiredInputs.filter((input) => !input.checkValidity());
    if (invalidInputs.length === 0) return true;

    invalidInputs.forEach((input) => input.setAttribute('aria-invalid', 'true'));

    // Focus first invalid field and set up listeners
    invalidInputs[0].focus();
    this.requiredFieldsChangeListener();

    return false;
  }

  /**
   * Listen for changes to required fields and remove error state
   */
requiredFieldsChangeListener() {
    const requiredInputs = this.querySelectorAll('[required]');
    requiredInputs.forEach((input) => {
      input.addEventListener(
        'input',
        () => input.checkValidity() && input.removeAttribute('aria-invalid'),
        { once: true }
      );
    });
  }

  handleOutOfStock(button) {
    const variantId = button.getAttribute('data-variant-id');
    const date = () => new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');

    // Simulate click on stock fulfilled button
    document.querySelector('button[gw-stock-fulfilled]')?.click();

    if (typeof Insider === 'undefined') return;

    Insider.track('events', [
      {
        event_name: 'notify-me',
        timestamp: date(),
        event_params: {
          product_id: variantId,
        },
      },
    ]);
  }

  /**
   * Toggle loading state on submit buttons
   * @param {boolean} loading
   */
  toggleLoading(loading) {
    this.submitButtons.forEach((button) => button.classList.toggle('loading', loading));
  }

  /**
   * Create form data
   * @returns {Object}
   */
  createFormData() {
    // Get all property inputs
    const propertyInputs = this.querySelectorAll('[name^="properties"]');
    const properties = {};

    // Build properties object from inputs
    propertyInputs.forEach((input) => {
      const propertyName = input.name.match(/properties\[(.*?)\]/)[1];
      properties[propertyName] = input.value;
    });

    return {
      items: [
        {
          id: this.variantIdInput.value,
          quantity: this.quantityInput.value,
          properties,
        },
      ],
    };
  }

  /**
   * Add to cart
   * @param {Object} formData
   * @returns {Object}
   */
  async addToCart(formData) {
    const response = await fetch(`${routes.cart_add_url}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Failed to add to cart');
    return data;
  }

  /**
   * Handle form success
   */
  onSuccess(itemData, quantity) {
    notyf.success(window.toast_translations.cart.success);
    pubsub.publish('cart:added', {
      items: itemData.items,
      quantity,
    });
  }

  /**
   * Handle form error
   * @param {Error} error - The error object
   */
  onError({ message }) {
    console.error(message);
    notyf.error(message);
  }
}

customElements.define('product-form', ProductForm);
